// glossary.ts - the tap-to-learn term corpus (S19), ported 1:1 from the locked mockup TERMS. Every
// poker term shown in the app has an entry here; <Term> opens the popover for its key. `example` is the
// "Here:" line (the mockup rendered "<b>Here:</b> ..."); the label is added structurally by the popover.
export interface GlossaryEntry {
  title: string;
  description: string;
  example: string;
}

export const GLOSSARY: Record<string, GlossaryEntry> = {
  position: {
    title: 'Position',
    description:
      'Where you sit relative to the button decides who acts last. Late seats like the Cutoff act after most players on every street after the flop, so you make decisions with more information than they had.',
    example: 'in the Cutoff you act last against the Vacuum all the way to the river.',
  },
  limp: {
    title: 'Limp',
    description:
      'Entering a pot by just calling the big blind instead of raising. Weak and passive players do it constantly because they want to see a cheap flop.',
    example: 'the Vacuum limped in, which is exactly the loose, passive pattern that lets you raise to isolate.',
  },
  vacuum: {
    title: 'The Vacuum (a calling station)',
    description:
      'A player who hates folding and almost never raises. He sucks up bets with any pair or draw. You beat him by value-betting more and bluffing far less, because he will not fold and rarely turns his hand into a bluff.',
    example:
      'a Vacuum who suddenly bets is showing a hand he likes, not a bluff. That is why his aggression is such loud information.',
  },
  isolate: {
    title: 'Isolate',
    description:
      'Raising to push other players out so you get the weak player heads up, in a pot you control from position. You are not trying to win it now, you are building a clean spot against the one opponent you want.',
    example: 'raising the limper folds the blinds and leaves you one on one with the Vacuum, in position.',
  },
  ladder: {
    title: 'The evidence ladder',
    description:
      'How sure a read should be. Level 1: one odd action, treat it as a question. Level 2: the same thing twice, start adjusting. Level 3: repeated plus a showdown, adjust for real. Level 4: the player changed after a loss, a drink, or a new stack, so old reads expire.',
    example:
      'the Vacuum betting climbs the ladder street by street. One bet is a question, two is a pattern, three is conviction.',
  },
  tpk: {
    title: 'Top pair, top kicker',
    description:
      'You paired the highest card on the board, the jack, and your other card, the ace, is the best possible side card. A strong one-pair hand: good enough to bet or call, not strong enough to want a giant pot.',
    example: 'A of spades, J of spades on J 8 4 is top pair, top kicker.',
  },
  range: {
    title: 'Range',
    description:
      'The whole set of hands a player could reasonably have right now, not one exact guess. You think in groups and cross off the ones his actions no longer fit.',
    example: 'the Vacuum starts mostly medium pairs and draws, then narrows toward value as he keeps betting.',
  },
  thin: {
    title: 'Thin value',
    description:
      'Betting or calling a hand that is only a little bit ahead, because enough slightly worse hands still pay you. You are not chasing a huge pot, just earning a bit more from hands you already beat.',
    example: 'calling the flop gets value from his weaker jacks and pairs that a raise would scare away.',
  },
  flatcall: {
    title: 'Just calling (flat call)',
    description:
      'Calling instead of raising, on purpose, to keep a weaker range in. Against a player who will not fold worse hands and will not bet again with air, raising only isolates you against the hands that beat you.',
    example: 'flatting the flop keeps his medium pairs in for value; raising would fold them out.',
  },
  blank: {
    title: 'Blank',
    description:
      'A community card that changes almost nothing: it does not complete draws or make new strong hands. When a blank comes and a player keeps betting big, the bet is about his existing hand, not the new card.',
    example: 'the 2 and then the 7 are blanks, so his continued betting is real strength, not the board helping him.',
  },
  barrel: {
    title: 'Barrel (second, third)',
    description:
      'Betting again on the next street. A second barrel is the turn bet, a third barrel is the river bet. Repeated barrels from a passive player are strong evidence, because passive players do not fire street after street without a hand.',
    example:
      'a Vacuum firing a second and third barrel is the pattern that moves the read from question to conviction.',
  },
  bluffcatch: {
    title: 'Bluff-catcher',
    description:
      'A hand that beats the bluffs an opponent might have but loses to his value bets. Calling is only right when he actually bluffs enough. Against a player who almost never bluffs, a bluff-catcher becomes a fold.',
    example: 'top pair catches bluffs, but a Vacuum has almost no bluffs, so the catch is a losing call. Fold.',
  },
  reqeq: {
    title: 'Required equity',
    description:
      'The share of the time you need the best hand for a call to break even. It is the amount you call divided by the final pot after you call.',
    example: '$30 to call divided by a $103 final pot is about 29 percent. Top pair is ahead far more often than that on the flop.',
  },
  firecracker: {
    title: 'The Firecracker (a loose, aggressive player)',
    description:
      'A player who plays too many hands and bets and raises far too often, with made hands, draws, and pure air alike. You beat him by calling down lighter than usual, because a large part of his betting range is bluffs, not value.',
    example:
      'a Firecracker who fires three barrels is usually firing missed draws and air, so your bluff-catcher becomes a call, not a fold.',
  },
};
